import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Heading, 
  Flex, 
  Spinner, 
  Alert, 
  AlertIcon, 
  Button, 
  Text, 
  Switch, 
  FormControl, 
  FormLabel, 
  Stack,
  Badge,
  Code,
  useDisclosure,
  Collapse,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  IconButton,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem
} from '@chakra-ui/react';
import Plot from 'react-plotly.js';
import { categoryColorMap } from '../../data/MockCategoryStats';
import useCategoryStatsStore from '../../stores/useCategoryStatsStore';
import { supabase } from '../../integrations/supabase/client';

/**
 * 分类统计图表组件
 * 显示各个分类及其文章数量的可视化
 */
const CategoryStatsChart = ({ showDebug = false }) => {
  // 使用统一的状态管理
  const { 
    stats, 
    isLoading, 
    error, 
    useMockData, 
    connectionStatus,
    isDevelopment,
    debugInfo,
    lastFetchTime,
    cacheExpiry,
    toggleMockData, 
    fetchCategoryStats,
    clearCache 
  } = useCategoryStatsStore();
  
  // 文章表调试状态
  const [articleTableInfo, setArticleTableInfo] = useState(null);
  const [isCheckingArticles, setIsCheckingArticles] = useState(false);
  
  // 控制调试面板显示
  const { isOpen: isDebugOpen, onToggle: toggleDebug } = useDisclosure({ 
    defaultIsOpen: isDevelopment 
  });
  
  // 组件加载时获取数据
  useEffect(() => {
    fetchCategoryStats();
  }, [fetchCategoryStats]);
  
  // 处理离线状态
  const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
  
  // 刷新数据，强制跳过缓存
  const refreshData = () => {
    fetchCategoryStats(false, true);
  };
  
  // 获取缓存状态文本
  const getCacheStatus = () => {
    if (!lastFetchTime) return '无缓存';
    
    const now = new Date().getTime();
    const cacheAge = now - lastFetchTime;
    const expiresIn = cacheExpiry - cacheAge;
    
    if (expiresIn <= 0) return '缓存已过期';
    
    // 将剩余时间转换为分钟和秒
    const minutes = Math.floor(expiresIn / (60 * 1000));
    const seconds = Math.floor((expiresIn % (60 * 1000)) / 1000);
    
    return `缓存有效，${minutes}分${seconds}秒后过期`;
  };
  
  // 直接检查文章表
  const checkArticleTable = async () => {
    setIsCheckingArticles(true);
    try {
      // 获取总条数
      const { count: totalCount, error: countError } = await supabase
        .from('article')
        .select('*', { count: 'exact', head: true });
      
      // 获取样本数据
      const { data: sampleData, error: sampleError } = await supabase
        .from('article')
        .select('article_id, feed_id, title')
        .limit(5);
      
      // 获取每个feed的文章数
      const { data: feedCounts, error: feedCountError } = await supabase
        .from('feed')
        .select('feed_id, title')
        .order('feed_id');
      
      let perFeedCounts = [];
      
      if (feedCounts && !feedCountError) {
        // 对每个feed计算文章数
        perFeedCounts = await Promise.all(feedCounts.slice(0, 5).map(async (feed) => {
          const { count } = await supabase
            .from('article')
            .select('article_id', { count: 'exact', head: true })
            .eq('feed_id', feed.feed_id);
            
          return {
            feed_id: feed.feed_id,
            title: feed.title,
            article_count: count || 0
          };
        }));
      }
      
      setArticleTableInfo({
        totalCount,
        countError,
        sampleData,
        sampleError,
        perFeedCounts,
        feedCountError,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      setArticleTableInfo({ error: err.message });
    } finally {
      setIsCheckingArticles(false);
    }
  };
  
  // 准备 Treemap 图表数据
  const prepareTreemapData = () => {
    if (!stats || stats.length === 0) {
      return [{
        type: 'treemap',
        labels: ['无数据'],
        parents: [''],
        values: [1],
        textinfo: "label+value",
        marker: { 
          colors: ['#CCCCCC']
        }
      }];
    }
    
    console.log('准备树图数据，源数据:', stats);
    
    // 创建树图数据
    const labels = [];    // 显示的标签
    const parents = [];   // 父节点
    const values = [];    // 值（文章数量）
    const colors = [];    // 颜色
    
    // 先添加根节点（分类）
    stats.forEach(cat => {
      labels.push(cat.category);
      parents.push('');
      
      // 计算分类下的总文章数
      const totalArticles = cat.feeds 
        ? cat.feeds.reduce((sum, feed) => sum + (feed.articleCount || 0), 0) 
        : 0;
      
      values.push(totalArticles);
      colors.push(getCategoryColor(cat.category));
    });
    
    // 再添加子节点（feed）
    stats.forEach(cat => {
      if (!cat.feeds || cat.feeds.length === 0) return;
      
      cat.feeds.forEach(feed => {
        if (feed.articleCount > 0) { // 只显示有文章的feed
          labels.push(feed.feedName);
          parents.push(cat.category);
          values.push(feed.articleCount);
          colors.push(getCategoryColor(cat.category));
        }
      });
    });
    
    console.log('树图数据准备完成:', {labels, parents, values});
    
    return [{
      type: 'treemap',
      labels,
      parents,
      values,
      textinfo: "label+value",
      marker: {
        colors
      },
      hoverinfo: 'label+value+percent parent',
      textposition: 'middle center',
      hoverlabel: {
        bgcolor: 'rgba(255, 255, 255, 0.9)',
        bordercolor: '#333',
        font: {
          family: 'Arial',
          size: 12,
          color: '#333'
        }
      }
    }];
  };
  
  // 根据分类获取颜色
  const getCategoryColor = (category) => {
    return categoryColorMap[category] || '#CCCCCC';
  };

  const layout = {
    title: useMockData ? '分类文章数量统计 (演示数据)' : '分类文章数量统计',
    height: 500,
    margin: {
      l: 10, 
      r: 10,
      b: 10,
      t: 50,
      pad: 4
    },
    showlegend: false, // 隐藏图例
  };

  const config = {
    responsive: true,
    displayModeBar: true,
    displaylogo: false,
    modeBarButtonsToRemove: ['lasso2d', 'select2d']
  };

  // 检查是否有任何feed有非零文章数量
  const hasArticles = stats.some(cat => 
    cat.feeds && cat.feeds.some(feed => feed.articleCount > 0)
  );
  
  // 检查是否有任何数据问题
  const hasDataIssues = !hasArticles && !useMockData && stats.length > 0;

  return (
    <Box 
      borderWidth="1px" 
      borderRadius="lg" 
      p={5} 
      boxShadow="md"
      bg="white"
      minHeight="600px"
    >
      <Stack direction={["column", "row"]} justify="space-between" align="center" mb={4}>
        <Flex align="center">
          <Heading as="h2" size="md">
            分类文章统计
          </Heading>
          <Stack direction="row" spacing={2} ml={2}>
            {useMockData && (
              <Badge colorScheme="blue">演示数据</Badge>
            )}
            {lastFetchTime && (
              <Tooltip label={getCacheStatus()}>
                <Badge colorScheme="green">已缓存</Badge>
              </Tooltip>
            )}
          </Stack>
        </Flex>
        
        <Stack direction={["column", "row"]} spacing={4} align="center">
          {isOffline && (
            <Alert status="warning" borderRadius="md" size="sm" py={1} px={2}>
              <AlertIcon />
              <Text fontSize="sm">网络已断开</Text>
            </Alert>
          )}
          
          <FormControl display="flex" alignItems="center" w="auto">
            <FormLabel htmlFor="mock-data-switch" mb="0" fontSize="sm">
              使用演示数据
            </FormLabel>
            <Switch 
              id="mock-data-switch" 
              isChecked={useMockData} 
              onChange={(e) => toggleMockData(e.target.checked)}
              isDisabled={isLoading}
            />
          </FormControl>
          
          <Menu>
            <MenuButton
              as={Button}
              size="sm"
              colorScheme="blue"
              isLoading={isLoading}
              isDisabled={isOffline && !useMockData}
            >
              操作
            </MenuButton>
            <MenuList>
              <MenuItem onClick={() => fetchCategoryStats()}>刷新数据 (使用缓存)</MenuItem>
              <MenuItem onClick={refreshData}>刷新数据 (忽略缓存)</MenuItem>
              <MenuItem onClick={clearCache}>清除缓存</MenuItem>
            </MenuList>
          </Menu>
          
          <Tooltip label="检查文章表">
            <IconButton
              aria-label="检查文章表"
              icon={<span>🔍</span>}
              size="sm"
              onClick={checkArticleTable}
              isLoading={isCheckingArticles}
              colorScheme="teal"
            />
          </Tooltip>
          
          <Button size="sm" onClick={toggleDebug} variant="outline">
            {isDebugOpen ? '隐藏' : '显示'}调试信息
          </Button>
        </Stack>
      </Stack>
      
      {/* 数据问题警告 */}
      {hasDataIssues && (
        <Alert status="warning" mb={4}>
          <AlertIcon />
          检测到真实数据中没有文章数量信息。请检查数据库中是否存在文章记录，或使用演示数据查看预期效果。
        </Alert>
      )}
      
      {isLoading ? (
        <Flex justify="center" align="center" height="500px">
          <Spinner size="xl" />
        </Flex>
      ) : error && !useMockData ? (
        <Alert status="error" borderRadius="md" mb={4}>
          <AlertIcon />
          数据加载失败: {error}
        </Alert>
      ) : stats.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          暂无统计数据
        </Alert>
      ) : (
        <Plot
          data={prepareTreemapData()}
          layout={layout}
          config={config}
          style={{ width: '100%', height: '500px' }}
        />
      )}
      
      {/* 调试信息面板 */}
      <Collapse in={isDebugOpen} animateOpacity>
        <Box mt={4} p={3} borderWidth="1px" borderRadius="md" bg="gray.50">
          <Heading as="h3" size="sm" mb={2}>调试信息</Heading>
          
          <Stack spacing={2}>
            <Text fontSize="sm">
              <strong>连接状态:</strong> 
              <Badge 
                ml={1} 
                colorScheme={connectionStatus.includes('成功') ? 'green' : 'orange'}
              >
                {connectionStatus}
              </Badge>
            </Text>
            <Text fontSize="sm">
              <strong>环境:</strong> {isDevelopment ? '开发' : '生产'}
            </Text>
            <Text fontSize="sm">
              <strong>数据模式:</strong> {useMockData ? '演示数据' : '真实数据'}
            </Text>
            <Text fontSize="sm">
              <strong>缓存状态:</strong> {getCacheStatus()}
            </Text>
            {lastFetchTime && (
              <Text fontSize="sm">
                <strong>上次获取时间:</strong> {new Date(lastFetchTime).toLocaleString()}
              </Text>
            )}
            <Text fontSize="sm">
              <strong>分类数:</strong> {stats.length}
            </Text>
            <Text fontSize="sm">
              <strong>总Feed数:</strong> {stats.reduce((acc, cat) => acc + (cat.feeds?.length || 0), 0)}
            </Text>
            <Text fontSize="sm">
              <strong>网络状态:</strong> {isOffline ? '离线' : '在线'}
            </Text>
            <Text fontSize="sm">
              <strong>有效文章数量:</strong> 
              <Badge 
                ml={1} 
                colorScheme={hasArticles ? "green" : "red"}
              >
                {hasArticles ? "是" : "否"}
              </Badge>
            </Text>
            
            {/* 文章表检查结果 */}
            {articleTableInfo && (
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontSize="sm">
                      <strong>文章表检查结果</strong>
                      <Badge ml={2} colorScheme={articleTableInfo.totalCount > 0 ? "green" : "red"}>
                        {articleTableInfo.totalCount || 0} 篇文章
                      </Badge>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Box fontSize="xs">
                    <Text mb={2}>检查时间: {articleTableInfo.timestamp}</Text>
                    
                    {articleTableInfo.error ? (
                      <Alert status="error" size="sm">
                        <AlertIcon />
                        检查失败: {articleTableInfo.error}
                      </Alert>
                    ) : (
                      <>
                        <Text mb={1} fontWeight="bold">文章总数: {articleTableInfo.totalCount || 0}</Text>
                        
                        <Text mt={2} fontWeight="bold">各Feed文章数量:</Text>
                        {articleTableInfo.perFeedCounts?.map((item, idx) => (
                          <Text key={idx} ml={2}>
                            Feed {item.feed_id} ({item.title}): {item.article_count} 篇
                          </Text>
                        ))}
                        
                        <Text mt={2} fontWeight="bold">样本数据:</Text>
                        <Code p={2} fontSize="xs" overflowX="auto" display="block">
                          {JSON.stringify(articleTableInfo.sampleData, null, 2)}
                        </Code>
                      </>
                    )}
                  </Box>
                </AccordionPanel>
              </AccordionItem>
            )}
            
            {/* 展开详细的数据结构 */}
            <Accordion allowToggle>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box flex="1" textAlign="left" fontSize="sm">
                      <strong>数据结构详情</strong>
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <Text fontSize="sm" fontWeight="bold" mb={1}>分类和文章数量:</Text>
                  {stats.map((cat, index) => (
                    <Box key={index} ml={2} mb={2}>
                      <Text fontSize="sm" fontWeight="bold">{cat.category}</Text>
                      {cat.feeds && cat.feeds.length > 0 ? (
                        cat.feeds.map((feed, fIndex) => (
                          <Text key={fIndex} fontSize="xs" ml={4}>
                            {feed.feedName}: <Badge colorScheme={feed.articleCount > 0 ? "green" : "red"}>
                              {feed.articleCount || 0} 篇文章
                            </Badge>
                          </Text>
                        ))
                      ) : (
                        <Text fontSize="xs" ml={4} color="red.500">无Feed数据</Text>
                      )}
                    </Box>
                  ))}
                </AccordionPanel>
              </AccordionItem>
              
              {/* 详细调试信息 */}
              {debugInfo && (
                <AccordionItem>
                  <h2>
                    <AccordionButton>
                      <Box flex="1" textAlign="left" fontSize="sm">
                        <strong>API调用详情</strong>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Code p={2} fontSize="xs" overflowX="auto" display="block">
                      {JSON.stringify(debugInfo, null, 2)}
                    </Code>
                  </AccordionPanel>
                </AccordionItem>
              )}
            </Accordion>
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
};

export default CategoryStatsChart; 